import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Search, X } from 'lucide-react';
import { useViewModelReady } from '../../../../hooks/useViewModel';
import { useTaskTypesToolViewModel } from './viewModel';
import { paths } from '../../../../routes/paths';

export function TaskTypesTool({ mobileNavTrigger }: { mobileNavTrigger?: React.ReactNode }) {
  const vm = useTaskTypesToolViewModel();
  useViewModelReady(vm.initialize);

  const hasFilter = vm.query.trim().length > 0;

  return (
    <section className="min-w-0">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Task Types</h1>
          <p className="mt-1 text-sm text-gray-500">
            Select a task type to edit its instructions and preview the task.
          </p>
        </div>
        {mobileNavTrigger}
      </div>

      {vm.busy ? null : vm.modules.length === 0 ? (
        <p className="text-sm text-gray-500">No task types registered yet.</p>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="relative w-full sm:min-w-[240px] sm:max-w-md sm:flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={vm.query}
                onChange={(e) => vm.setQuery(e.target.value)}
                placeholder="Filter by name…"
                aria-label="Filter task types by name"
                className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {vm.query && (
                <button
                  type="button"
                  onClick={() => vm.setQuery('')}
                  aria-label="Clear filter"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="text-xs text-gray-500">
              {hasFilter
                ? `${vm.filteredModules.length} of ${vm.modules.length}`
                : `${vm.modules.length} total`}
            </div>
          </div>

          {vm.filteredModules.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-sm text-gray-500">
              No task types match "{vm.query}".
            </p>
          ) : (
            <ul className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white">
              {vm.filteredModules.map((module) => (
                <li key={module.id}>
                  <Link
                    to={paths.adminTaskType(module.id)}
                    className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">{module.displayName}</p>
                      {module.description && (
                        <p className="text-sm text-gray-600">{module.description}</p>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
